import * as React from 'react';
import { Container } from '../../../components/web/container/container';
import { WebButton as Button } from '../../../components/web/button/web-button';
import { IconNext } from '../../../components/app/icons/next';
import { cn } from '../../../utils/tailwind';
import { SignupButton } from '../../../components/web/button/signup-button';
import { useStickyPanel } from '../../../hooks/use-sticky-panel';
import styles from './cta.module.css';

interface CtaPropsInterface {
    title: string;
    signup?: boolean;
    titleGrey?: React.ReactNode;
    href?: string;
    buttonIcon?: React.ReactNode;
    actions?: React.ReactNode;
    small?: boolean;
    compact?: boolean;
    buttonText?: string;
    onClick?: () => void;
}

export const Cta: React.FC<CtaPropsInterface> = ({
    title,
    signup = false,
    titleGrey,
    small = false,
    compact = true,
    buttonText,
    onClick,
    href,
    buttonIcon,
    actions,
}) => {
    const panelRef = useStickyPanel();
    const ActionButton = signup ? SignupButton : Button;
    if (compact) {
        return (
            <section data-homepage-cta className={styles.section}>
                <div ref={panelRef} data-cta-panel className={`${styles.panel} relative isolate overflow-hidden`}>
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-[28rem] top-1/2 -z-[1] h-full w-[95rem] -translate-y-1/2 -rotate-[12deg] bg-[url('/images/marketing/monery-hero-ribbon-mobile-800.webp')] md:bg-[url('/images/marketing/monery-hero-ribbon-mobile-1280.webp')] vl:bg-[url('/images/marketing/monery-hero-ribbon-v3.png')] bg-cover bg-center opacity-[0.3] [mask-image:radial-gradient(ellipse_at_center,black,transparent_68%)]"
                    />
                    <Container>
                        <div
                            className={`${styles.dividers} flex flex-col items-start gap-[3.2rem] py-[4rem] vl:flex-row vl:items-center vl:justify-between vl:gap-[6rem] vl:py-[5.6rem]`}
                        >
                            <div className="max-w-[68rem]">
                                {titleGrey && (
                                    <p className="mb-[1.2rem] text-[1.7rem] leading-relaxed text-web-copy">
                                        {titleGrey}
                                    </p>
                                )}
                                <h2 className="text-[3rem] font-bold leading-[1.15] tracking-[-0.025em] text-brand-secondary [text-wrap:balance] md:text-[4rem]">
                                    {title}
                                </h2>
                            </div>
                            {actions ??
                                (buttonText && (
                                    <div className="shrink-0">
                                        <ActionButton
                                            variant="primary"
                                            size={{ xs: 'medium', sm: 'vlarge' }}
                                            icon={buttonIcon ?? <IconNext />}
                                            href={href}
                                            iconGap="small"
                                            onClick={onClick}
                                        >
                                            {buttonText}
                                        </ActionButton>
                                    </div>
                                ))}
                        </div>
                    </Container>
                </div>
            </section>
        );
    }
    return (
        <div
            className={cn(
                'flex flex-col pt-[13rem] text-center text-[4.8rem] leading-[5.7rem] font-bold max-[768px]:py-[6rem] max-[768px]:text-[2.8rem] max-[768px]:leading-[3.3rem]',
                small && 'text-[3rem] leading-[3.9rem] max-[768px]:text-[1.8rem] max-[768px]:leading-[2.4rem]',
            )}
        >
            <Container>
                <div className="text-web-copy">{titleGrey}</div>
                <div className="text-brand-secondary">{title}</div>
                {buttonText && (
                    <div className="mt-[2.5rem]">
                        <ActionButton variant="primary" size="large" onClick={onClick} href={href}>
                            {buttonText}
                        </ActionButton>
                    </div>
                )}
            </Container>
        </div>
    );
};
